import {
	DeviceLastEvents,
	DevicesCoreData,
	MapDevices,
	SelectedGroupChild,
	SelectedGroups,
	TrailData,
	UpdateLastReportData,
	UpdateMapDeviceData,
	UpdateMapDeviceDisplayProperties,
} from '@/OptimusRoutes/components/mapDevices/types/mapDevicesReducer.types';
import { ActionType, IAction } from '@/types/actionType';

interface SetMapDevicesAction extends IAction<MapDevices> {
	type: ActionType.MAP_DEVICES_SET_DEVICES;
	payload: {
		devices: MapDevices;
		devicesCoreData: DevicesCoreData;
	};
}

interface UpdateMapDevicesAction extends IAction<UpdateMapDeviceData> {
	type: ActionType.MAP_DEVICES_UPDATE_DEVICE;
	payload: UpdateMapDeviceData;
}

interface UpdateLastReportAction extends IAction<UpdateLastReportData> {
	type: ActionType.MAP_DEVICES_UPDATE_LAST_REPORT;
	payload: UpdateLastReportData;
}

interface ClearDevicesAction {
	type: ActionType.MAP_DEVICES_CLEAR_DEVICES;
}

interface ClearAllDataAction {
	type: ActionType.MAP_DEVICES_CLEAR_ALL_DATA;
}

interface ClearAllDataAction {
	type: ActionType.MAP_DEVICES_CLEAR_ALL_DATA;
}

interface SetLoadingStatusAction extends IAction<boolean> {
	type: ActionType.MAP_DEVICES_SET_LOADING_STATUS;
	payload: boolean;
}

interface SetLoadingSegmentedTrailAction extends IAction<boolean> {
	type: ActionType.MAP_DEVICES_SET_LOADING_SEGMENTED_TRAIL;
	payload: boolean;
}

interface SetSelectedGroupAction extends IAction<SelectedGroups> {
	type: ActionType.MAP_DEVICES_SET_SELECTED_GROUPS;
	payload: SelectedGroups;
}
interface SetSelectedUnitAction extends IAction<number | string> {
	type: ActionType.MAP_DEVICES_SET_SELECTED_UNIT;
	payload: number | string;
}

interface SetSelectedGroupChildAction extends IAction<SelectedGroupChild> {
	type: ActionType.MAP_DEVICES_SET_SELECTED_GROUP_CHILD;
	payload: SelectedGroupChild;
}

interface RemoveSelectedGroupAction extends IAction<string> {
	type: ActionType.MAP_DEVICES_REMOVE_SELECTED_GROUP;
	payload: string;
}

interface RemoveSelectedChildGroupAction extends IAction<SelectedGroupChild> {
	type: ActionType.MAP_DEVICES_REMOVE_SELECTED_GROUP_CHILD;
	payload: SelectedGroupChild;
}

interface SetDeviceLastEventsAction extends IAction<DeviceLastEvents> {
	type: ActionType.MAP_DEVICES_SET_LAST_EVENTS;
	payload: DeviceLastEvents;
}

interface SetSelectedDateAction extends IAction<string> {
	type: ActionType.MAP_DEVICES_SET_SELECTED_DATE;
	payload: string;
}

interface UpdateTrailDataAction extends IAction<TrailData> {
	type: ActionType.MAP_DEVICES_UPDATE_TRAIL_DATA;
	payload: TrailData;
}

interface UpdateMapDeviceHistoricalMarkerAction extends IAction<UpdateMapDeviceData> {
	type: ActionType.MAP_DEVICES_UPDATE_HISTORICAL_MARKER;
	payload: UpdateMapDeviceData;
}

interface UpdateMapDevicePropertiesAction extends IAction<UpdateMapDeviceDisplayProperties> {
	type: ActionType.MAP_DEVICES_UPDATE_DEVICE_PROPERTIES;
	payload: UpdateMapDeviceDisplayProperties;
}
export type Action =
	| SetMapDevicesAction
	| UpdateMapDevicesAction
	| UpdateMapDeviceHistoricalMarkerAction
	| ClearDevicesAction
	| ClearAllDataAction
	| SetLoadingStatusAction
	| SetLoadingSegmentedTrailAction
	| SetSelectedGroupAction
	| SetSelectedUnitAction
	| SetSelectedGroupChildAction
	| RemoveSelectedGroupAction
	| RemoveSelectedChildGroupAction
	| SetDeviceLastEventsAction
	| UpdateLastReportAction
	| SetSelectedDateAction
	| UpdateTrailDataAction
	| UpdateMapDevicePropertiesAction;

export const SetMapDevices = (devices: MapDevices) => {
	let devicesCoreData: DevicesCoreData = {};

	Object.values(devices).forEach((device) => {
		devicesCoreData[device.id] = {
			id: device.id,
			description: device.description,
			pin: device.pin,
			isTransmiting: !!device.utcDate,
			camara: device?.camara,
			video: device?.video,
			panic: device?.panic,
			ianaTimezone: device.ianaTimezone,
			thermometersConfig: device?.thermometersConfig,
			shared: device?.shared,
			/**
			 * These props change constantly and are used only to see if the properties exist to show them at the dropdown
			 */
			tanks: device?.tanks && Object.values(device?.tanks).some((item) => item),
			thermometers: device?.thermometers && Object.values(device?.thermometers).some((item) => item),
			modelProfileId: device.modelProfileId,
			timeZoneId: device.timeZoneId,
			icon: device.icon,
		};
	});
	return {
		type: ActionType.MAP_DEVICES_SET_DEVICES,
		payload: {
			devices,
			devicesCoreData,
		},
	};
};

export const UpdateMapDevice = (data: UpdateMapDeviceData) => {
	return {
		type: ActionType.MAP_DEVICES_UPDATE_DEVICE,
		payload: data,
	};
};

export const UpdateLastReport = (data: UpdateLastReportData) => {
	return {
		type: ActionType.MAP_DEVICES_UPDATE_LAST_REPORT,
		payload: data,
	};
};

export const ClearMapDevices = () => ({
	type: ActionType.MAP_DEVICES_CLEAR_DEVICES,
});

export const ClearAllMapDevicesData = () => ({
	type: ActionType.MAP_DEVICES_CLEAR_ALL_DATA,
});

export const SetLoadingStatus = (isLoading: boolean) => ({
	type: ActionType.MAP_DEVICES_SET_LOADING_STATUS,
	payload: isLoading,
});

export const SetLoadingSegmentedTrail = (isLoading: boolean) => ({
	type: ActionType.MAP_DEVICES_SET_LOADING_SEGMENTED_TRAIL,
	payload: isLoading,
});

export const SetSelectedUnit = (unitId: number | string) => {
	return {
		type: ActionType.MAP_DEVICES_SET_SELECTED_UNIT,
		payload: unitId,
	};
};

export const SetSelectedGroups = (groups: SelectedGroups) => {
	return {
		type: ActionType.MAP_DEVICES_SET_SELECTED_GROUPS,
		payload: groups,
	};
};

export const SetSelectedGroupChild = (selectedGroupChild: SelectedGroupChild) => {
	return {
		type: ActionType.MAP_DEVICES_SET_SELECTED_GROUP_CHILD,
		payload: selectedGroupChild,
	};
};

export const RemoveSelectedGroup = (groupId: string) => {
	return {
		type: ActionType.MAP_DEVICES_REMOVE_SELECTED_GROUP,
		payload: groupId,
	};
};

export const RemoveSelectedGroupChild = (selectedGroupChild: Omit<SelectedGroupChild, 'total'>) => {
	return {
		type: ActionType.MAP_DEVICES_REMOVE_SELECTED_GROUP_CHILD,
		payload: selectedGroupChild,
	};
};

export const SetDeviceLastEvents = ({ deviceId, lastEvents }: DeviceLastEvents) => {
	return {
		type: ActionType.MAP_DEVICES_SET_LAST_EVENTS,
		payload: { deviceId, lastEvents },
	};
};

export const SetSelectedDate = (date: string) => {
	return {
		type: ActionType.MAP_DEVICES_SET_SELECTED_DATE,
		payload: date,
	};
};

export const UpdateTrailData = (data: TrailData) => {
	return {
		type: ActionType.MAP_DEVICES_UPDATE_TRAIL_DATA,
		payload: data,
	};
};

export const UpdateMapDeviceHistoricalMarker = (data: UpdateMapDeviceData) => {
	return {
		type: ActionType.MAP_DEVICES_UPDATE_HISTORICAL_MARKER,
		payload: data,
	};
};

export const UpdateMapDeviceProperties = (data: UpdateMapDeviceDisplayProperties) => {
	return {
		type: ActionType.MAP_DEVICES_UPDATE_DEVICE_PROPERTIES,
		payload: data,
	};
};
